import React, { useState, useMemo, useEffect } from 'react';
import {
  Tooltip,
  LineChart,
  ResponsiveContainer,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  CartesianGrid,
} from 'recharts';
import mixpanel from 'mixpanel-browser';

import {
  Grid,
  Typography,
  ClickAwayListener,
  IconButton,
  Link,
  Button,
  Backdrop,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import TooltipCard from '../TooltipCard';
import InfoTooltip from '../../Tooltips/InfoTooltip';
import { infoIconStyle, cardTitle, infoIconButton } from 'src/App/Dashboards/Market/styles/style';
import {
  COLOR_PRIMARY_DARK,
  COLOR_TOOLTIP_BACKGROUND,
  COLOR_BLACK,
  COLOR_GREEN,
  COLOR_YELLOW,
  COLOR_RED_LIGHT,
  COLOR_E,
  COLOR_ACCENT_2_CONTRAST,
  LIGHT_GREY,
  percentageNumberNameMap,
  timePreiodNumberNameMap,
  exchangeRates,
} from 'src/const';
import {
  formatCurrency,
  isEmpty,
  parseGraphVal,
  makeDownloadableBlob,
  abbreviateNumber,
  getMoMYoYChangeColor,
  getSeasonality,
  getSeasonalityBarColor,
  getStartingEndingDate,
  processMarketHistoricalData,
  getKeyAsPerPercentileAndTitle,
} from 'src/utils/functions';
import { DigestCardGraphData, Revenue, ADR, OccupancyRate } from 'src/interfaces/idashboard';
import { RearViewKeyMetricsPropTypes } from 'src/interfaces/iproptypes';
import TooltipNew from 'src/App/Pages/EstimatorPage/UtilityComponents/TooltipNew';
import { WATER_MARK_COLOR } from 'src/colors';
import DisabledBlurCard from 'src/App/Pages/EstimatorPage/UtilityComponents/BlurCard';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useStyles = makeStyles((theme: Theme) => ({
  cardRoot: {
    background: '#fff',
    padding: '16px',
  },
  linearProgress: {
    width: '100%',
    marginBottom: 20,
    color: COLOR_PRIMARY_DARK,
  },
  sampleText: {
    color: COLOR_E,
    fontWeight: 600,
    transform: 'rotate(-30deg)',
    msTransform: 'rotate(-30deg)' /* IE 9 */,
    webkitTransform: 'rotate(-30deg),' /* Opera, Chrome, and Safari */,
  },
}));

const loadingStyle = makeStyles((theme: Theme) => ({
  colorPrimary: {
    backgroundColor: (props: any) => props.backgroundColor,
  },
  barColorPrimary: {
    backgroundColor: COLOR_TOOLTIP_BACKGROUND,
  },
}));

const CustomTooltip = ({ active, payload, label, classes, percentile, currency }) => {
  if (active && payload && payload.length > 0) {
    const _payload = payload[0].payload;
    if (
      _payload.Revenue === 'missing' ||
      _payload['Nightly Rate'] === 'missing' ||
      _payload['Occupancy Rate'] === 'missing'
    ) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {_payload.timePeriod}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Data Unavailable
            </Typography>
          </CardContent>
        </Card>
      );
    } else if (
      _payload.Revenue === 'inactive' ||
      _payload['Nightly Rate'] === 'inactive' ||
      _payload['Occupancy Rate'] === 'inactive'
    ) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {_payload.timePeriod}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Inactive
            </Typography>
          </CardContent>
        </Card>
      );
    } else {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {payload[0].payload.timePeriod}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              <span style={{ color: percentile === 0.9 ? COLOR_GREEN : 'black' }}>
                90th percentile : {payload[0].unit}
                {formatCurrency(String(payload[0].payload['0.9']))}
              </span>
              <br />
              <span style={{ color: percentile === 0.75 ? COLOR_GREEN : 'black' }}>
                75th percentile : {payload[0].unit}
                {formatCurrency(String(payload[0].payload['0.75']))}
              </span>
              <br />
              <span style={{ color: percentile === 0.5 ? COLOR_GREEN : 'black' }}>
                Median : {payload[0].unit}
                {formatCurrency(String(payload[0].payload['0.5']))}
              </span>
              <br />
              <span style={{ color: percentile === 0.25 ? COLOR_GREEN : 'black' }}>
                25th Percentile : {payload[0].unit}
                {formatCurrency(String(payload[0].payload['0.25']))}
              </span>
            </Typography>
          </CardContent>
        </Card>
      );
    }
  }

  return null;
};

const CustomizedDot = props => {
  //linechart
  const { cx, cy, value, color, getHighLowPoints } = props;

  const limit = getHighLowPoints();

  const size = 6;
  const smallSize = 4;

  if (value === limit.high || value === limit.low) {
    return (
      <svg x={cx - size} y={cy - size} width={size * 2} height={size * 2} fill="white">
        <g transform={`translate(${size} ${size})`}>
          <circle r={size} fill={color} />
          {/* <circle r={size / 2} fill="white" /> */}
        </g>
      </svg>
    );
  } else if (value !== null && value >= 0) {
    return (
      <svg
        x={cx - smallSize}
        y={cy - smallSize}
        width={smallSize * 2}
        height={smallSize * 2}
        fill="white"
      >
        <g transform={`translate(${smallSize} ${smallSize})`}>
          <circle r={smallSize} fill={color} />
          <circle r={smallSize / 2} fill="white" />
        </g>
      </svg>
    );
  } else {
    return (
      <svg
        x={cx - smallSize}
        y={cy - smallSize}
        width={smallSize * 2}
        height={smallSize * 2}
        fill="white"
      >
        <g transform={`translate(${smallSize} ${smallSize})`}>
          <circle r={smallSize} fill={'#0000'} />
        </g>
      </svg>
    );
  }
};

const RearViewKeyMetrics = ({
  title,
  graphData,
  aggregateData,
  tooltip,
  color,
  isDisabled,
  currency,
  unit,
  metricsKey,
  percentile,
  timePeriod,
  setPercentile,
  setTimePeriod,
  onDisabledOptionClick
}: RearViewKeyMetricsPropTypes) => {
  const classes = useStyles();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  let seasonalityData = {} as {
    highestRevenue: Revenue;
    lowestRevenue: Revenue;
    seasonality: number;
  };
  if (title.includes('Revenue') && graphData.length > 0) {
    seasonalityData = getSeasonality(graphData, 'estimator', `${percentile}`);
  }

  const getHighLowPoints = () => {
    let low = Infinity;
    let high = -Infinity;
    graphData.forEach(data => {
      if (data[`${metricsKey}`] != 'missing' && data[`${metricsKey}`] != 'inactive') {
        if (high < data[`${metricsKey}`]) {
          high = data[`${metricsKey}`];
        }
        if (low > data[`${metricsKey}`]) {
          low = data[`${metricsKey}`];
        }
      }
    });
    return { high, low };
  };

  const combineFormatterYAxis = (val: number) => {
    if (title.includes('Revenue')) {
      return abbreviateNumber(val);
    } else {
      return abbreviateNumber(val, false);
    }
  };

  const handlePercentileChange = (event: SelectChangeEvent) => {
    const value = parseFloat(event.target.value) * 100;
    setPercentile(String(value));
  };

  const handleTimePeriodChange = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    setTimePeriod(value);
  };

  let blurGraph = {};

  const hoverGraph = () => {
    mixpanel.track('checkTooltipOnInvestmentDashboard');
  };

  const makeKeyMatricesObjectToArray = graphData => {
    const resultArray: [string, string][] = [];

    resultArray.push(['Dates', title + ` (${unit})`]);
    for (const row of graphData) {
      resultArray.push([row['date'], row[percentile]]);
    }

    return resultArray;
  };

  const getKeyMetricsCSVDataAsArray = () => {
    const keyMatricesCSVDataAsArray = makeKeyMatricesObjectToArray(graphData);
    makeDownloadableBlob(
      keyMatricesCSVDataAsArray,
      `${title}-${timePreiodNumberNameMap[timePeriod]}-${percentageNumberNameMap[percentile]}`,
      false
    );

    return null;
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      item
      xs={12}
      className={classes.cardRoot}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ marginBottom: '20px' }}
      >
        {timePeriod && (
          <Typography variant="h2" style={cardTitle}>
            {title}
            <TooltipNew
              title={title}
              content={tooltip.content[0]}
              enterTouchDelay={0}
              leaveTouchDelay={6000}
            >
              <IconButton style={infoIconButton} onClick={() => setIsTooltipOpen(prev => !prev)}>
                <InfoOutlinedIcon style={infoIconStyle} />
              </IconButton>
            </TooltipNew>
            {/* <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
              <InfoTooltip
                title={
                  <TooltipCard
                    title={title}
                    content={tooltip.content[0]}
                    contentAdditional={tooltip.content.length > 1 ? tooltip.content[1] : ''}
                    contentAdditional2={tooltip.content.length > 2 ? tooltip.content[2] : ''}
                    link=""
                  />
                }
                enterTouchDelay={0}
                leaveTouchDelay={6000}
                open={isTooltipOpen}
                onOpen={() => setIsTooltipOpen(true)}
                onClose={() => setIsTooltipOpen(false)}
              >
                <IconButton style={infoIconButton} onClick={() => setIsTooltipOpen(prev => !prev)}>
                  <InfoOutlinedIcon style={infoIconStyle} />
                </IconButton>
              </InfoTooltip>
            </ClickAwayListener> */}
            <span style={{ fontWeight: 'bold', margin: '0px 2px' }}>
              {timePeriod === 12 &&
                (title.includes('Occupancy')
                  ? aggregateData
                  : exchangeRates[currency].symbol + formatCurrency(String(aggregateData)))}
            </span>
            {/* <InfoTooltip                            
              title={
                <TooltipCard
                  title={'EXPORT'}
                  content={`This will export the ${
                    isDisabled ? '' : 'monthly'
                  } ${title?.toLowerCase()}s as a CSV file which has 2 columns, dates, and ${title}.`}
                  contentAdditional={
                    isDisabled ? 'Please unlock this market to download the CSV file.' : ''
                  }
                />
              }
              enterTouchDelay={0}
              leaveTouchDelay={6000}
            >
              <IconButton style={infoIconButton}>
                <CloudDownloadOutlinedIcon
                  style={{
                    ...infoIconStyle,
                    fontSize: 24,
                    color: isDisabled ? LIGHT_GREY : COLOR_ACCENT_2_CONTRAST,
                  }}
                  onClick={() => getKeyMetricsCSVDataAsArray()}
                />
              </IconButton>
            </InfoTooltip> */}
          </Typography>
        )}
      </Grid>

      <Grid container direction="row" justifyContent="flex-start" spacing={0}>
        {title.includes('Revenue') && !isDisabled && !isEmpty(seasonalityData) && (
          <Grid item xs={1} container>
            <InfoTooltip
              title={
                <TooltipCard
                  title={`Seasonality: ${seasonalityData.seasonality}%`}
                  content={`Highest Revenue: ${unit}${formatCurrency(
                    String(seasonalityData.highestRevenue.revenue as number)
                  )} (${seasonalityData.highestRevenue.date})`}
                  contentAdditional={`Lowest Revenue: ${unit}${formatCurrency(
                    String(seasonalityData.lowestRevenue.revenue as number)
                  )} (${seasonalityData.lowestRevenue.date})`}
                />
              }
            >
              <div
                style={{
                  width: 10,
                  borderRadius: 10,
                  height: `${seasonalityData.seasonality}%`,
                  background: getSeasonalityBarColor(seasonalityData.seasonality),
                }}
              ></div>
            </InfoTooltip>
          </Grid>
        )}

        <Grid
          container
          item
          xs={title.includes('Revenue') && !isDisabled && !isEmpty(seasonalityData) ? 11 : 12}
          direction="row"
          style={{ position: 'relative', top: '0', left: '0' }}
        >
          <Grid
            item
            xs={12}
            style={
              isDisabled
                ? {
                    filter: 'blur(3px)',
                  }
                : {}
            }
          >
            <ResponsiveContainer height={100} width="100%">
              <LineChart
                data={graphData}
                style={blurGraph}
                onMouseEnter={hoverGraph}
                margin={{ top: 10, right: 10, bottom: 10, left: 0 }}
              >
                <CartesianGrid strokeDasharray="5 5" vertical={false} />
                <Line
                  animationDuration={150}
                  isAnimationActive={false}
                  type="monotone"
                  dataKey={percentile}
                  stroke={color}
                  strokeWidth={3}
                  unit={unit}
                  dot={<CustomizedDot color={color} getHighLowPoints={getHighLowPoints} />}
                />
                <XAxis dataKey="timePeriod" hide={true} />
                <YAxis
                  dataKey={percentile}
                  axisLine={false}
                  tickCount={5}
                  tickLine={false}
                  style={{
                    fontSize: '0.75rem',
                    fontFamily: 'Roboto',
                  }}
                  domain={title.includes('Occupancy') && [0, 100]}
                  label={{
                    value: title.includes('Occupancy') ? '%' : exchangeRates[currency].symbol,
                    position: 'insideLeft',
                    textAnchor: 'middle',
                    style: { fontSize: '0.75rem', fontFamily: 'Roboto', color: '#000000DE' },
                  }}
                  tickFormatter={combineFormatterYAxis}
                />

                <ReferenceLine
                  x={graphData.length > 0 ? getStartingEndingDate(graphData[0].date, true) : ''}
                  stroke={color}
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeDasharray="4 12"
                />
                <ReferenceLine
                  x={
                    graphData.length > 0
                      ? getStartingEndingDate(graphData[graphData.length - 1].date, true)
                      : ''
                  }
                  stroke={color}
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeDasharray="4 12"
                />
                <Tooltip
                  position={{ x: 0, y: 60 }}
                  content={
                    <CustomTooltip
                      classes={classes}
                      active
                      payload
                      label
                      percentile={percentile}
                      currency={currency}
                    />
                  }
                  wrapperStyle={{ zIndex: 100000 }}
                  filterNull={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Grid>

          {isDisabled && <DisabledBlurCard subscriptionType="Pro" onDisabledOptionClick={onDisabledOptionClick} />}

          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{
              fontFamily: 'roboto',
              fontSize: '10px',
              color: '#555',
            }}
          >
            {!isDisabled && (
              <>
                <Grid item>{graphData.length > 0 && getStartingEndingDate(graphData[0].date)}</Grid>
                <Grid item>
                  {graphData.length > 0 &&
                    getStartingEndingDate(graphData[graphData.length - 1].date)}
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ marginTop: '20px', padding: 0 }}
      >
        <FormControl
          variant="standard"
          size="small"
          sx={{ width: 150, marginBottom: '5px', padding: 0 }}
        >
          <InputLabel id="percentile-select">Percentile</InputLabel>
          <Select
            labelId="percentile-select"
            id="percentile-select"
            value={`${percentile}`}
            onChange={handlePercentileChange}
            label="Percentile"
          >
            <MenuItem value={0.9}>90th percentile</MenuItem>
            <MenuItem value={0.75}>75th percentile</MenuItem>
            <MenuItem value={0.5}>Median</MenuItem>
            <MenuItem value={0.25}>25th percentile</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default RearViewKeyMetrics;
